import { useCallback, type MouseEvent } from 'react';
import { assetRecordDisplayTitle } from '../components/AssetGetByIdView';
import { useI18n } from '../i18n';
import { favoriteIdForAsset } from './favoriteAssetsStorage';
import { useFavoriteAssets } from './FavoriteAssetsContext';
import styles from './FavoriteAssetHeartButton.module.css';

/** Heroicons-style heart — SVG stays sharp on Android WebView (Unicode ♡ + text-stroke does not). */
const HEART_PATH =
  'M12 21.35 10.55 20.03C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

type FavoriteAssetHeartButtonProps = {
  asset: Record<string, unknown>;
  className?: string;
  activeClassName?: string;
};

export function FavoriteAssetHeartButton({
  asset,
  className,
  activeClassName,
}: FavoriteAssetHeartButtonProps) {
  const { t } = useI18n();
  const { isFavorite, toggleFavorite } = useFavoriteAssets();
  const id = favoriteIdForAsset(asset);
  const favorited = isFavorite(asset);
  const title = assetRecordDisplayTitle(asset);

  const onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      toggleFavorite(asset);
    },
    [asset, toggleFavorite],
  );

  if (!id) {
    return null;
  }

  const classes = [className, favorited ? activeClassName : ''].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={[classes, favorited ? styles.favorited : styles.unfavorited].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-pressed={favorited}
      aria-label={
        favorited ? t('favorites.remove', { title }) : t('favorites.add', { title })
      }
    >
      <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden focusable="false">
        <path className={styles.outline} d={HEART_PATH} />
        <path className={favorited ? styles.pathFilled : styles.path} d={HEART_PATH} />
      </svg>
    </button>
  );
}
